/**
 * useOrchestrationAIGenerate Hook
 *
 * Orchestrates the AI generation flow for orchestration actions.
 * Manages the full lifecycle: generate → validate → preview → confirm → apply.
 *
 * Calls /ai/generate with orchestration-specific modes, handles SSE streaming,
 * validates returned actions, and applies them to the orchestration via backend endpoints.
 *
 * Supports three generation modes:
 *   - "orchestration_populate": existing — populate empty orchestration with steps
 *   - "orchestration_edit":     existing — edit steps in an existing orchestration
 *   - "orchestration_create":   one-shot — create a brand-new orchestration end-to-end
 *                               (env+dataset bound from modal selectors, no prior steps).
 *                               In this mode `orchestration` may be `null` and the
 *                               applier sources the new id from a `create_orchestration`
 *                               action's response (Phase 0 in `applyOrchestrationActions`).
 *                               Snapshot capture is skipped (no prior state to revert),
 *                               so undo is unavailable for one-shot creates by design.
 *
 * @module features/orchestration/hooks/useOrchestrationAIGenerate
 * @requires Requirements 1.3, 1.4, 1.5, 1.6, 2.1, 4.1, 4.2, 4.3, 4.8, 9.1, 9.3, 9.4, 9.6, 11.1
 */

import { useState, useCallback, useRef } from 'react';
import { validateOrchestrationActions } from '../services/orchestrationActionValidator';
import { applyOrchestrationActions } from '../services/orchestrationActionApplier';
import { captureSnapshot, getSnapshot, restoreSnapshot, clearSnapshot } from '../services/snapshotManager';
import { buildDiagnosticPrompt, buildFixContext } from '../services/feedbackLoopService';
import { separateChainingActions } from '../services/variableChainingEngine';

import { readToken } from '../../../shared/auth/session';

/** Base URL for the AI service (Python FastAPI) */
const AI_BASE_URL = import.meta.env.VITE_AI_SERVICE_URL || 'http://localhost:8000';

/** Default timeout for generation requests (60 seconds — orchestration prompts can be complex) */
const GENERATION_TIMEOUT_MS = 60_000;

/** Get auth token from sessionStorage */
const getToken = () => readToken();

/** Build Authorization header with JWT bearer token */
const authHeaders = () => {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
};

/**
 * Merge two AbortSignals into a single signal that aborts
 * when either of the source signals aborts.
 *
 * @param {AbortSignal} signal1
 * @param {AbortSignal} signal2
 * @returns {AbortSignal}
 */
function mergeAbortSignals(signal1, signal2) {
  const controller = new AbortController();
  const onAbort = () => controller.abort();

  if (signal1.aborted || signal2.aborted) {
    controller.abort();
    return controller.signal;
  }

  signal1.addEventListener('abort', onAbort, { once: true });
  signal2.addEventListener('abort', onAbort, { once: true });

  return controller.signal;
}

/**
 * Parse SSE stream from the AI generation endpoint.
 * Handles event types: "token", "done", "error".
 *
 * @param {ReadableStreamDefaultReader} reader - Stream reader from fetch response
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onToken - Called with each streamed token
 * @param {Function} callbacks.onComplete - Called with parsed actions array on "done"
 * @param {Function} callbacks.onError - Called with Error on "error" event or parse failure
 * @returns {Promise<void>}
 */
async function parseSSEStream(reader, { onToken, onComplete, onError }) {
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulatedTokens = '';
  // [AI-ORCH-DEBUG] Track event counts for diagnostics
  let tokenEventCount = 0;
  let doneEventCount = 0;
  let errorEventCount = 0;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // [AI-ORCH-DEBUG] Stream closed by server
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by double newlines
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const part of parts) {
        if (!part.trim()) continue;

        const lines = part.split('\n');
        let eventType = 'message';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            eventData += line.slice(5).trim();
          }
        }

        if (eventType === 'token') {
          tokenEventCount++;
          try {
            const parsed = JSON.parse(eventData);
            const token = parsed.token !== undefined ? parsed.token : eventData;
            accumulatedTokens += token;
            onToken?.(token);
          } catch {
            accumulatedTokens += eventData;
            onToken?.(eventData);
          }
        } else if (eventType === 'done') {
          doneEventCount++;
          // [AI-ORCH-DEBUG] Done event arrived
          try {
            const parsed = JSON.parse(eventData);
            const actions = parsed.actions || parsed;
            onComplete?.(Array.isArray(actions) ? actions : []);
          } catch (parseErr) {
            // [AI-ORCH-DEBUG] Done payload JSON parse failed
            console.warn('[AI-ORCH-DEBUG] sse.done_parse_failed', {
              error: parseErr.message,
              dataPreview: eventData.slice(0, 300),
            });
            // Try accumulated tokens as fallback
            try {
              const fallback = JSON.parse(accumulatedTokens);
              const actions = fallback.actions || fallback;
              onComplete?.(Array.isArray(actions) ? actions : []);
            } catch {
              const parseErrFinal = new Error('AI returned an invalid response. Try again.');
              parseErrFinal.code = 'PARSE_ERROR';
              onError?.(parseErrFinal);
            }
          }
        } else if (eventType === 'error') {
          errorEventCount++;
          // [AI-ORCH-DEBUG] Error event from backend
          console.warn('[AI-ORCH-DEBUG] sse.error_received', {
            dataPreview: eventData.slice(0, 300),
          });
          try {
            const parsed = JSON.parse(eventData);
            const serverErr = new Error(parsed.message || parsed.detail || 'Generation failed');
            serverErr.code = parsed.code || 'SERVER_ERROR';
            serverErr.data = parsed;
            onError?.(serverErr);
          } catch {
            const serverErr = new Error(eventData || 'Generation failed');
            serverErr.code = 'SERVER_ERROR';
            onError?.(serverErr);
          }
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const lines = buffer.split('\n');
      let eventType = 'message';
      let eventData = '';

      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          eventData += line.slice(5).trim();
        }
      }

      if (eventType === 'done' && eventData) {
        try {
          const parsed = JSON.parse(eventData);
          const actions = parsed.actions || parsed;
          onComplete?.(Array.isArray(actions) ? actions : []);
        } catch {
          // Ignore — stream ended without valid done event
        }
      }
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      const abortErr = new Error('Generation was cancelled.');
      abortErr.code = 'ABORTED';
      onError?.(abortErr);
    } else {
      const streamErr = new Error('Generation was interrupted. Try again.');
      streamErr.code = 'STREAM_ERROR';
      onError?.(streamErr);
    }
  }
}

/**
 * Hook that orchestrates the AI generation flow for orchestration actions.
 * Handles the full lifecycle: generate → validate → preview → confirm → apply.
 *
 * @param {Object|null} orchestration - Current orchestration object (with orchestration_id,
 *   steps, etc.) — `null` when invoked from the orchestration list view in
 *   one-shot create mode (no orchestration selected yet).
 * @param {Function} onStepListRefresh - Callback to refresh the step list after actions are applied
 * @returns {{
 *   generate: (prompt: string, mentions: Array, mode: string, context: Object) => Promise<void>,
 *   generateFix: (failedSteps: Array) => Promise<void>,
 *   apply: () => Promise<void>,
 *   undo: () => Promise<void>,
 *   cancel: () => void,
 *   isGenerating: boolean,
 *   isApplying: boolean,
 *   isUndoing: boolean,
 *   previewActions: Array|null,
 *   chainingActions: Array,
 *   error: string|null,
 *   warnings: Array,
 *   createdOrchestrationId: number|null,
 * }}
 */
export function useOrchestrationAIGenerate(orchestration, onStepListRefresh) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [previewActions, setPreviewActions] = useState(null);
  const [chainingActions, setChainingActions] = useState([]);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  // Set to the new orchestration's id when a one-shot create succeeds; the
  // modal reads this to call selectOrchestration(...) and refresh the list
  // afterward (Req 10.3). Null in populate/edit mode and reset between runs.
  const [createdOrchestrationId, setCreatedOrchestrationId] = useState(null);

  /** @type {React.MutableRefObject<AbortController|null>} */
  const abortControllerRef = useRef(null);

  // Stores the user-supplied context from the most recent generate() call so that
  // apply() can override the AI's hallucinated IDs in `create_orchestration` actions
  // with the correct user-selected values (environment_id, dataset_id, project_id).
  const lastGenerateContextRef = useRef(null);

  /**
   * Abort any currently active generation stream.
   */
  const abortCurrentStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Start AI generation for orchestration actions.
   *
   * Sends a POST to /ai/generate with orchestration mode, prompt, mentions,
   * and LivePageData context. Parses the SSE stream and validates returned actions.
   *
   * In `orchestration_create` mode, validates that `context.environment.id` is
   * set BEFORE issuing the network call (Req 4.8). When the modal selectors
   * have not picked an environment, the hook surfaces an error and returns
   * without contacting the AI service. The modal will display the error and
   * keep the submit button disabled.
   *
   * `context.environment` and `context.dataset` are forwarded as supplied by the
   * caller (built from the modal selectors per Req 4.1–4.3): env id+name when an
   * environment is selected, dataset id+name when a dataset is selected, and the
   * dataset key omitted or null otherwise.
   *
   * @param {string} prompt - User's generation prompt
   * @param {Array} mentions - Structured entity mentions from @autocomplete
   * @param {string} mode - "orchestration_populate" | "orchestration_edit" | "orchestration_create"
   * @param {Object} context - LivePageData context object
   */
  const generate = useCallback(async (prompt, mentions, mode, context) => {
    // Cancel any in-progress generation
    if (abortControllerRef.current) {
      abortCurrentStream();
    }

    // Reset state
    setError(null);
    setWarnings([]);
    setPreviewActions(null);
    setCreatedOrchestrationId(null);
    setIsGenerating(true);

    // Store the user-supplied context so apply() can use the correct IDs
    lastGenerateContextRef.current = context || null;

    // Empty-environment submission gate (Req 4.8). In create mode the
    // environment selector is required: the new orchestration cannot be
    // bound without a target environment id. We validate before the
    // network call so the AI service is never invoked with a missing env,
    // and the modal can keep its submit button disabled until the user
    // picks one.
    if (mode === 'orchestration_create') {
      const envId = context?.environment?.id;
      if (envId === undefined || envId === null || envId === '') {
        setError('Select an environment before generating an orchestration.');
        setIsGenerating(false);
        abortControllerRef.current = null;
        return;
      }
    }

    // Create a new AbortController for this generation
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Create a timeout AbortController
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), GENERATION_TIMEOUT_MS);

    // Combine caller signal and timeout signal
    const combinedSignal = mergeAbortSignals(controller.signal, timeoutController.signal);

    const url = `${AI_BASE_URL}/generate`;

    // [AI-ORCH-DEBUG] About to POST

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          mode,
          prompt,
          context: context || null,
          agent_type: 'pro',
          stream: true,
          mentions: mentions && mentions.length > 0 ? mentions : null,
        }),
        signal: combinedSignal,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        if (timeoutController.signal.aborted && !controller.signal.aborted) {
          setError('Generation timed out. Try a simpler prompt or try again.');
        } else {
          // User-initiated cancel — no error display
          setError(null);
        }
      } else {
        setError(`Network error: ${err.message}`);
      }
      setIsGenerating(false);
      abortControllerRef.current = null;
      return;
    }

    clearTimeout(timeoutId);

    // [AI-ORCH-DEBUG] Response received

    // Handle non-OK responses
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch {
        // Ignore parse errors on error responses
      }

      console.warn('[AI-ORCH-DEBUG] hook.generate.non_ok', errorData);

      const errorMessage = errorData.detail || errorData.message || errorData.error || `HTTP ${response.status}`;
      setError(errorMessage);
      setIsGenerating(false);
      abortControllerRef.current = null;
      return;
    }

    // Parse the SSE stream
    const reader = response.body.getReader();

    await parseSSEStream(reader, {
      onToken: () => {
        // Streaming tokens — could be used for progressive UI in the future
      },
      onComplete: (rawActions) => {
        // [AI-ORCH-DEBUG] Validation start
        const rawTypes = (rawActions || []).reduce((acc, a) => {
          const t = a?.type || 'unknown';
          acc[t] = (acc[t] || 0) + 1;
          return acc;
        }, {});

        // Validate actions before showing preview
        const validActions = validateOrchestrationActions(rawActions);
        const discardedCount = rawActions.length - validActions.length;

        // [AI-ORCH-DEBUG] Validation result

        if (validActions.length === 0) {
          console.warn('[AI-ORCH-DEBUG] hook.generate.no_valid_actions', {
            rawSample: rawActions.slice(0, 3),
          });
          setError('AI returned no valid orchestration actions. Try rephrasing your prompt.');
          setIsGenerating(false);
          abortControllerRef.current = null;
          return;
        }

        // Create-mode integrity gate (Req 9.4). When validation drops the
        // only `create_orchestration` action — for example because it had a
        // malformed schema or duplicates were collapsed and the survivor
        // failed the schema check — we cannot create the orchestration the
        // user asked for. Surface a user-facing error and skip applying:
        // attaching the remaining add_step / script actions to nothing
        // would either silently no-op or attach them to an unrelated
        // orchestration. Neither outcome is desirable.
        if (mode === 'orchestration_create') {
          const hasCreateAction = validActions.some(
            (a) => a.type === 'create_orchestration'
          );
          if (!hasCreateAction) {
            setError(
              'AI did not return a valid orchestration creation. Try rephrasing your prompt.'
            );
            setIsGenerating(false);
            abortControllerRef.current = null;
            return;
          }
        }

        // Track warnings for discarded actions
        const newWarnings = [];
        if (discardedCount > 0) {
          newWarnings.push(
            `${discardedCount} action(s) were filtered out due to invalid structure.`
          );
        }

        // Separate chaining actions from user-requested actions
        const { userActions, chainingActions: chainActions } = separateChainingActions(validActions);

        setWarnings(newWarnings);
        setPreviewActions(userActions);
        setChainingActions(chainActions);
        setIsGenerating(false);
        abortControllerRef.current = null;
      },
      onError: (err) => {
        if (err.code === 'ABORTED') {
          // User-initiated cancel — no error display
          setError(null);
        } else {
          setError(err.message || 'Generation failed. Try again.');
        }
        setIsGenerating(false);
        abortControllerRef.current = null;
      },
    });
  }, [abortCurrentStream]);

  /**
   * Generate fix actions for failed orchestration steps.
   *
   * Sends failure data to /ai/generate with mode 'orchestration_edit',
   * a system-generated diagnostic prompt, and execution context.
   * Filters returned actions to only target failed steps or their dependencies.
   *
   * @param {Array<FailedStepData>} failedSteps - Failed step data from collectFailedStepData()
   * Requirements: 3.4, 4.1, 4.3, 5.1
   */
  const generateFix = useCallback(async (failedSteps) => {
    if (!failedSteps || failedSteps.length === 0) {
      setError('No failures to fix — all steps passed.');
      return;
    }

    // Cancel any in-progress generation
    if (abortControllerRef.current) {
      abortCurrentStream();
    }

    // Reset state
    setError(null);
    setWarnings([]);
    setPreviewActions(null);
    setIsGenerating(true);

    // Create a new AbortController for this generation
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Create a timeout AbortController
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), GENERATION_TIMEOUT_MS);

    // Combine caller signal and timeout signal
    const combinedSignal = mergeAbortSignals(controller.signal, timeoutController.signal);

    // Build the diagnostic prompt and fix context from failed step data
    const prompt = buildDiagnosticPrompt(failedSteps);
    const context = buildFixContext(orchestration, failedSteps, []);

    const url = `${AI_BASE_URL}/generate`;

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          mode: 'orchestration_edit',
          prompt,
          context,
          agent_type: 'pro',
          stream: true,
          mentions: null,
        }),
        signal: combinedSignal,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        if (timeoutController.signal.aborted && !controller.signal.aborted) {
          setError('Generation timed out. Try a simpler prompt or try again.');
        } else {
          // User-initiated cancel — no error display
          setError(null);
        }
      } else {
        setError(`Network error: ${err.message}`);
      }
      setIsGenerating(false);
      abortControllerRef.current = null;
      return;
    }

    clearTimeout(timeoutId);

    // Handle non-OK responses
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch {
        // Ignore parse errors on error responses
      }

      const errorMessage = errorData.detail || errorData.message || errorData.error || `HTTP ${response.status}`;
      setError(errorMessage);
      setIsGenerating(false);
      abortControllerRef.current = null;
      return;
    }

    // Build set of valid target step orders (failed steps + their dependencies)
    const failedStepOrders = new Set(failedSteps.map((fs) => fs.stepOrder));
    // Dependencies: steps that come before any failed step (upstream providers)
    const allSteps = orchestration?.steps || [];
    const dependencyOrders = new Set();
    for (const step of allSteps) {
      const stepOrder = step.step_order;
      // A step is a dependency if it precedes any failed step
      for (const failedOrder of failedStepOrders) {
        if (stepOrder < failedOrder) {
          dependencyOrders.add(stepOrder);
        }
      }
    }
    const allowedStepOrders = new Set([...failedStepOrders, ...dependencyOrders]);

    // Parse the SSE stream
    const reader = response.body.getReader();

    await parseSSEStream(reader, {
      onToken: () => {
        // Streaming tokens — could be used for progressive UI in the future
      },
      onComplete: (rawActions) => {
        // Validate actions structure
        const validActions = validateOrchestrationActions(rawActions);

        // Filter to only keep actions targeting failed steps or their dependencies
        const filteredActions = validActions.filter((action) => {
          const stepOrder = action.value?.step_order;
          if (stepOrder === undefined || stepOrder === null) {
            // Actions without step_order (e.g., add_orchestration_step) pass through
            return true;
          }
          return allowedStepOrders.has(stepOrder);
        });

        const discardedByValidation = rawActions.length - validActions.length;
        const discardedByScope = validActions.length - filteredActions.length;

        if (filteredActions.length === 0) {
          setError('AI could not generate valid fixes for the failing steps. Try re-running or adjusting step configuration manually.');
          setIsGenerating(false);
          abortControllerRef.current = null;
          return;
        }

        // Track warnings for discarded actions
        const newWarnings = [];
        if (discardedByValidation > 0) {
          newWarnings.push(
            `${discardedByValidation} action(s) were filtered out due to invalid structure.`
          );
        }
        if (discardedByScope > 0) {
          newWarnings.push(
            `${discardedByScope} action(s) were filtered out because they targeted steps that did not fail.`
          );
        }

        setWarnings(newWarnings);
        setPreviewActions(filteredActions);
        setIsGenerating(false);
        abortControllerRef.current = null;
      },
      onError: (err) => {
        if (err.code === 'ABORTED') {
          // User-initiated cancel — no error display
          setError(null);
        } else {
          setError(err.message || 'Fix generation failed. Try again.');
        }
        setIsGenerating(false);
        abortControllerRef.current = null;
      },
    });
  }, [orchestration, abortCurrentStream]);

  /**
   * Apply the previewed actions to the orchestration.
   *
   * Calls applyOrchestrationActions with the validated preview actions,
   * then refreshes the step list on success.
   *
   * Branches on the presence of a `create_orchestration` action in the
   * preview:
   *   - Present (one-shot create mode): pass `orchestrationId = null` and
   *     let the applier's Phase 0 source the new id from the create
   *     response. Snapshot capture is skipped — there is no prior state to
   *     revert, so undo is unavailable for one-shot creates by design.
   *   - Absent (populate / edit / fix): use `orchestration.orchestration_id`
   *     and capture a snapshot so the user can undo subsequent edits.
   *
   * On a Phase-0 create failure (`results.aborted`), we keep `previewActions`
   * intact and surface the backend error so the modal stays open and the
   * user can either retry or cancel (Req 7.4).
   */
  const apply = useCallback(async () => {
    if (!previewActions || previewActions.length === 0) {
      setError('No actions to apply.');
      return;
    }

    // Detect one-shot create mode by the presence of a `create_orchestration`
    // action in the preview. The validator already collapsed duplicates to
    // the first one (Req 3.4) and dropped malformed entries; if any survived,
    // it's authoritative.
    const createAction = previewActions.find(
      (a) => a.type === 'create_orchestration'
    );
    const isCreateMode = !!createAction;

    // In create mode, override the AI's hallucinated IDs with the user's actual
    // selections from the generate context. The AI may return internal/wrong IDs
    // for project_id, environment_id, and dataset_id. The user's selections from
    // the modal are authoritative.
    if (isCreateMode && lastGenerateContextRef.current) {
      const ctx = lastGenerateContextRef.current;
      if (ctx.environment?.id) {
        createAction.value.environment_id = ctx.environment.id;
      }
      if (ctx.dataset?.id) {
        createAction.value.dataset_id = ctx.dataset.id;
      }
      if (ctx.project?.id) {
        createAction.value.project_id = ctx.project.id;
      }
    }

    const orchestrationId = orchestration?.orchestration_id ?? null;
    if (!isCreateMode && !orchestrationId) {
      setError('No orchestration selected.');
      return;
    }

    setIsApplying(true);
    setError(null);

    // Initialize warnings before snapshot capture (Req 6.1, 6.4, 8.3)
    const newWarnings = [...warnings];

    // Capture snapshot before applying actions (Req 6.1, 6.4) — only when
    // editing an existing orchestration. One-shot create mode has no prior
    // state to revert to and the `orchestrationId` is not yet known until
    // Phase 0 completes, so snapshot capture is skipped here.
    if (!isCreateMode) {
      const currentSteps = orchestration?.steps || [];
      const snapshotSaved = captureSnapshot(orchestrationId, currentSteps);
      if (!snapshotSaved) {
        // QuotaExceededError — warn user but proceed
        newWarnings.push('Undo will not be available for this action');
      }
    }

    try {
      // Pass `null` orchestrationId in create mode; the applier's Phase 0
      // resolves the target id from the create response.
      const results = await applyOrchestrationActions(
        previewActions,
        isCreateMode ? null : orchestrationId
      );

      // Surface createdOrchestrationId so the modal can call
      // selectOrchestration(...) and refresh the orchestration list.
      if (results.createdOrchestrationId) {
        setCreatedOrchestrationId(results.createdOrchestrationId);
      }

      // Phase-0 abort: the create_orchestration call itself failed and the
      // applier short-circuited the rest of the sequence (Req 7.3). Keep
      // previewActions intact so the modal can surface the error without
      // auto-closing (Req 7.4) and let the user retry or cancel.
      if (results.aborted) {
        const failure = results.failed?.[0];
        setError(failure?.error || 'Failed to create orchestration.');
        setIsApplying(false);
        return;
      }

      // Update warnings with any failures
      if (results.failed.length > 0) {
        for (const failure of results.failed) {
          newWarnings.push(
            `Failed to apply ${failure.action.type}: ${failure.error}`
          );
        }
      }

      setWarnings(newWarnings);
      setPreviewActions(null);
      setChainingActions([]);
      setIsApplying(false);

      // Refresh the step list to show updated state
      if (onStepListRefresh) {
        onStepListRefresh();
      }
    } catch (err) {
      setError(err.message || 'Failed to apply actions.');
      setIsApplying(false);
    }
  }, [previewActions, orchestration, warnings, onStepListRefresh]);

  /**
   * Undo the last AI action by restoring from the stored snapshot.
   *
   * Retrieves the snapshot for the current orchestration, diffs against current
   * state, and restores changed steps via backend update endpoints.
   * Clears the snapshot on full success (single-level undo).
   * Retains snapshot on partial failure so the user can retry.
   *
   * Requirements: 7.3, 7.4, 7.5, 7.6, 7.7
   */
  const undo = useCallback(async () => {
    const orchestrationId = orchestration?.orchestration_id;
    if (!orchestrationId) {
      setError('No orchestration selected.');
      return;
    }

    const snapshot = getSnapshot(orchestrationId);
    if (!snapshot) {
      setError('No snapshot available to undo.');
      return;
    }

    setIsUndoing(true);
    setError(null);
    setWarnings([]);

    try {
      const currentSteps = orchestration?.steps || [];
      const result = await restoreSnapshot(orchestrationId, snapshot, currentSteps);

      if (result.failed.length === 0) {
        // Full success — clear snapshot (single-level undo)
        clearSnapshot(orchestrationId);
        setWarnings(['AI changes have been reverted successfully.']);
        if (onStepListRefresh) {
          onStepListRefresh();
        }
      } else {
        // Partial failure — retain snapshot for retry
        const failedNumbers = result.failed.map(f => `Step ${f.stepOrder}`).join(', ');
        setWarnings([
          `Partially reverted (${result.reverted} steps). Could not revert: ${failedNumbers}. You can retry.`
        ]);
        if (onStepListRefresh) {
          onStepListRefresh();
        }
      }
    } catch (err) {
      setError(err.message || 'Undo failed. Try again.');
    } finally {
      setIsUndoing(false);
    }
  }, [orchestration, onStepListRefresh]);

  /**
   * Cancel the current generation or discard preview actions.
   *
   * If generating: aborts the stream.
   * If previewing: discards the preview actions.
   */
  const cancel = useCallback(() => {
    // Abort any active stream
    abortCurrentStream();

    // Discard preview actions
    setPreviewActions(null);
    setChainingActions([]);
    setCreatedOrchestrationId(null);
    setIsGenerating(false);
    setIsApplying(false);
    setError(null);
    setWarnings([]);
  }, [abortCurrentStream]);

  return {
    // State
    isGenerating,
    isApplying,
    isUndoing,
    previewActions,
    chainingActions,
    error,
    warnings,
    createdOrchestrationId,

    // Actions
    generate,
    generateFix,
    apply,
    undo,
    cancel,
  };
}

export default useOrchestrationAIGenerate;
